'use client';

import { useCallback, useEffect, useRef, useState, type JSX } from 'react';
import { usePathname } from 'next/navigation';
import { useTranslations } from 'next-intl';
import { cn } from '@/lib/utils';

/**
 * ページ遷移中に画面最上部へ出す進捗バー。
 *
 * App Router のナビゲーションは loading 境界の無い区間ではブロッキングになり、
 * リンクを押してからサーバーの応答が返るまで画面が一切変化しない。押せたことを
 * すぐ返すために、クリックを拾った時点で計測を始める。
 *
 * 開始の検知は document 全体の click（capture 段階）と popstate。next-intl の
 * `Link` も最終的には `<a>` を描画するので、これで通常の遷移は拾える。
 * `router.push()` のようなプログラム遷移だけは拾えないため、呼ぶ側が
 * `startNavigationProgress()` を明示的に叩く。
 *
 * 終了の検知は「URL が変わったこと」。`usePathname` は next-intl 版だとロケール
 * 接頭辞が落ちて `/` と `/en` を区別できないので、ここでは next/navigation の
 * 生のパスを使う。クエリだけが変わる遷移は pathname が動かないため href も
 * 併せて監視する。
 */

/** 遷移開始の通知先。マウント中の NavigationProgress が自分の start を登録する。 */
const startListeners = new Set<() => void>();

/** `router.push()` など、`<a>` のクリックを伴わない遷移の直前に呼ぶ。 */
export function startNavigationProgress(): void {
  for (const listener of startListeners) {
    listener();
  }
}

type Phase = 'idle' | 'running' | 'finishing';

/**
 * バーを出すまでの猶予。これより速い遷移では一瞬光って消えるだけでうるさいので、
 * 何も出さずに終わらせる。
 */
const REVEAL_DELAY_MS = 180;
/** 応答が返らないまま出しっぱなしにならないための上限。 */
const RUNNING_LIMIT_MS = 10_000;
/** 100% まで伸びきるのを見せてから畳むまでの待ち（width の transition と揃える）。 */
const FINISHING_MS = 250;
/** 消えたあとに幅を戻すまでの待ち（opacity の transition と揃える）。 */
const RESET_MS = 350;

export function NavigationProgress(): JSX.Element {
  const pathname = usePathname();
  const t = useTranslations('navigation');
  const [phase, setPhase] = useState<Phase>('idle');
  const [visible, setVisible] = useState<boolean>(false);
  const [value, setValue] = useState<number>(0);
  // start / finish は effect の依存に入るので、判定は state ではなく ref で行う
  // （setState の updater 内で副作用を起こさないため）。
  const phaseRef = useRef<Phase>('idle');
  const visibleRef = useRef<boolean>(false);
  const revealTimerRef = useRef<number | null>(null);
  const startHrefRef = useRef<string>('');

  const clearRevealTimer = useCallback(() => {
    if (revealTimerRef.current !== null) {
      window.clearTimeout(revealTimerRef.current);
      revealTimerRef.current = null;
    }
  }, []);

  const start = useCallback(() => {
    if (phaseRef.current === 'running') {
      return;
    }
    phaseRef.current = 'running';
    startHrefRef.current = window.location.href;
    setPhase('running');
    setValue(8);
    clearRevealTimer();
    revealTimerRef.current = window.setTimeout(() => {
      revealTimerRef.current = null;
      visibleRef.current = true;
      setVisible(true);
    }, REVEAL_DELAY_MS);
  }, [clearRevealTimer]);

  const finish = useCallback(() => {
    if (phaseRef.current !== 'running') {
      return;
    }
    clearRevealTimer();
    // まだ出していないなら、そのまま何も見せずに終える
    if (!visibleRef.current) {
      phaseRef.current = 'idle';
      setPhase('idle');
      setValue(0);
      return;
    }
    phaseRef.current = 'finishing';
    setPhase('finishing');
    setValue(100);
  }, [clearRevealTimer]);

  useEffect(() => {
    startListeners.add(start);

    const handleClick = (event: MouseEvent) => {
      // 修飾キー付き・中クリックは別タブで開くので遷移しない
      if (event.defaultPrevented || event.button !== 0) return;
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;

      const eventTarget = event.target;
      if (!(eventTarget instanceof Element)) return;
      const anchor = eventTarget.closest('a');
      if (!(anchor instanceof HTMLAnchorElement)) return;
      if (anchor.target && anchor.target !== '_self') return;
      if (anchor.hasAttribute('download')) return;

      const href = anchor.getAttribute('href');
      if (!href || href.startsWith('#')) return;

      let destination: URL;
      try {
        destination = new URL(anchor.href, window.location.href);
      } catch {
        return;
      }
      // 外部リンクはページ遷移の進捗を出しても意味がない
      if (destination.origin !== window.location.origin) return;
      // 同一ページ内のハッシュ移動は遷移ではない
      if (
        destination.pathname === window.location.pathname &&
        destination.search === window.location.search
      ) {
        return;
      }

      start();
    };

    // popstate は URL が変わった後に発火するので href の比較では終了を拾えない。
    // pathname の変化（大半のケース）か、上限のタイムアウトで閉じる。
    const handlePopState = () => start();

    document.addEventListener('click', handleClick, true);
    window.addEventListener('popstate', handlePopState);
    return () => {
      startListeners.delete(start);
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('popstate', handlePopState);
    };
  }, [start]);

  // アンマウント時に予約済みのタイマーを落とす
  useEffect(() => clearRevealTimer, [clearRevealTimer]);

  // 実際の残り時間は分からないので 90% へ漸近させる。
  useEffect(() => {
    if (phase !== 'running') return;
    const timer = window.setInterval(() => {
      setValue((current) =>
        current >= 90 ? current : current + Math.max(0.5, (90 - current) * 0.08),
      );
    }, 140);
    return () => window.clearInterval(timer);
  }, [phase]);

  // URL が変わったら完了。pathname の変化は即時に拾える。
  // pathname を effect 内では読んでいないが、「遷移が完了した」唯一の合図なので依存に残す。
  // biome-ignore lint/correctness/useExhaustiveDependencies: 上記のとおり意図的な依存
  useEffect(() => {
    finish();
  }, [pathname, finish]);

  // クエリだけの遷移と、応答が返らないケースの保険。
  useEffect(() => {
    if (phase !== 'running') return;
    const poll = window.setInterval(() => {
      if (window.location.href !== startHrefRef.current) {
        finish();
      }
    }, 120);
    const limit = window.setTimeout(finish, RUNNING_LIMIT_MS);
    return () => {
      window.clearInterval(poll);
      window.clearTimeout(limit);
    };
  }, [phase, finish]);

  // 100% まで伸ばしきってからフェードアウトし、消えてから幅を戻す。
  useEffect(() => {
    if (phase !== 'finishing') return;
    const hide = window.setTimeout(() => {
      visibleRef.current = false;
      setVisible(false);
      phaseRef.current = 'idle';
      setPhase('idle');
    }, FINISHING_MS);
    const reset = window.setTimeout(() => setValue(0), FINISHING_MS + RESET_MS);
    return () => {
      window.clearTimeout(hide);
      window.clearTimeout(reset);
    };
  }, [phase]);

  return (
    <>
      <div
        aria-hidden="true"
        data-navigation-progress={phase}
        className={cn(
          // ヘッダー (z-[45]) とダイアログ (z-70) より前面に出す。
          // 背景を敷くのは、ヘッダー最上部の帯が bg-primary/80 で、ライトテーマだと
          // bg-primary のバーが同系色に埋もれて見えなくなるため（実測で確認）。
          'pointer-events-none fixed inset-x-0 top-0 z-80 h-[3px] bg-background/80 transition-opacity duration-300',
          visible ? 'opacity-100' : 'opacity-0',
        )}
      >
        <div
          className="h-full rounded-r-full bg-primary shadow-[0_0_10px_2px_var(--color-primary)] transition-[width] duration-200 ease-out"
          style={{ width: `${value}%` }}
        />
      </div>
      {/* 視覚に頼らない利用者へ遷移中であることを伝える */}
      <span role="status" aria-live="polite" className="sr-only">
        {visible ? t('loading') : ''}
      </span>
    </>
  );
}
